
import { Component, Prop } from "vue-property-decorator";
import Vue from "vue";
import { throttle } from "lodash";
import { px } from "@/util/css";
import { SomeNonNullable } from "@/types/utils";
import Icon from "@/components/UI/Icon.vue";
import { uiStore } from "@/store/store";

enum ForeignDocumentTypeLabels {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  breakdance_header = "header",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  breakdance_footer = "footer"
}

@Component({
  components: { Icon }
})
export default class ForeignDocumentsOverlay extends Vue {
  @Prop()
  canvasElement!: HTMLElement;

  @Prop()
  iframeDocument!: Document;

  @Prop()
  elementsToWatch!: NodeListOf<HTMLElement>;

  hoveredForeignDocumentDomElement: HTMLElement | null = null;
  hoveredForeignDocumentDomElementCoords: DOMRect | null = null;

  isScrolling = false;
  isScrollingCleanupTimerId: NodeJS.Timeout | null = null;
  throttledHandleIframeScroll = throttle(this.handleIframeScroll, 120);
  throttledHandleWatchedElementMouseMove = throttle(
    this.handleWatchedElementMouseMove,
    60
  );

  get showOverlay(): boolean {
    return uiStore.hasFullAccess;
    //    return this.isThereHoveredElementAndItsCoords() && !this.isScrolling;
  }

  get foreignDocumentBuilderUrl(): string | undefined {
    return this.hoveredForeignDocumentDomElement?.dataset
      .breakdanceForeignDocumentBuilderUrl;
  }

  get foreignDocumentTypeLabel(): string {
    const documentType = this.hoveredForeignDocumentDomElement?.dataset
      .breakdanceForeignDocumentType as
      | keyof typeof ForeignDocumentTypeLabels
      | undefined;

    return documentType ? ForeignDocumentTypeLabels[documentType] ?? "" : "";
  }

  get foreignDocumentTitle(): string {
    const documentTitle = this.hoveredForeignDocumentDomElement?.dataset
      .breakdanceForeignDocumentTitle as string | undefined;
    return documentTitle ? documentTitle : "Unknown Document";
  }

  get overlayStyles() {
    if (!this.isThereHoveredElementAndItsCoords()) {
      return undefined;
    }

    const { top, left, height, width } =
      this.hoveredForeignDocumentDomElementCoords;

    return {
      display: "flex",
      top: px(top),
      left: px(left),
      height: px(height),
      width: px(width)
    };
  }

  mounted() {
    this.addEventListenersToIframeDocument();
    this.addEventListenersToWatchedElements();
  }

  beforeDestroy() {
    this.removeEventListenersFromIframeDocument();
    this.removeEventListenersFromWatchedElements();
  }

  addEventListenersToIframeDocument() {
    this.iframeDocument.addEventListener(
      "scroll",
      this.throttledHandleIframeScroll
    );
  }

  removeEventListenersFromIframeDocument() {
    this.iframeDocument.removeEventListener(
      "scroll",
      this.throttledHandleIframeScroll
    );
  }

  addEventListenersToWatchedElements() {
    this.elementsToWatch.forEach(node => {
      node.addEventListener(
        "mousemove",
        this.throttledHandleWatchedElementMouseMove
      );
      node.addEventListener("mouseleave", this.handleWatchedElementMouseLeave);
    });
  }

  removeEventListenersFromWatchedElements() {
    this.elementsToWatch.forEach(node => {
      node.removeEventListener(
        "mousemove",
        this.throttledHandleWatchedElementMouseMove
      );
      node.removeEventListener(
        "mouseleave",
        this.handleWatchedElementMouseLeave
      );
    });
  }

  isThereHoveredElementAndItsCoords(): this is SomeNonNullable<
    this,
    | "hoveredForeignDocumentDomElement"
    | "hoveredForeignDocumentDomElementCoords"
  > {
    return (
      this.hoveredForeignDocumentDomElement !== null &&
      this.hoveredForeignDocumentDomElementCoords !== null
    );
  }

  setHoveredForeignDocumentDomElement(value: HTMLElement | null) {
    this.hoveredForeignDocumentDomElement = value;
    this.hoveredForeignDocumentDomElementCoords = this.getCoords();
  }

  getCoords() {
    const el = this.hoveredForeignDocumentDomElement;
    if (el?.childElementCount === 1) {
      // The child element could be position fixed,
      // so we need to use its coords instead of the foreign element.
      const child = el.firstElementChild as HTMLElement;
      return child.getBoundingClientRect();
    } else {
      return el?.getBoundingClientRect() ?? null;
    }
  }

  setIsScrolling(isScrolling: boolean) {
    if (isScrolling) {
      this.hoveredForeignDocumentDomElement = null;
      this.hoveredForeignDocumentDomElementCoords = null;
      this.removeEventListenersFromWatchedElements();
    } else {
      this.addEventListenersToWatchedElements();
    }
    this.isScrolling = isScrolling;
  }

  handleIframeScroll() {
    this.setIsScrolling(true);

    if (this.isScrollingCleanupTimerId) {
      clearTimeout(this.isScrollingCleanupTimerId);
    }

    this.isScrollingCleanupTimerId = setTimeout(
      () => this.setIsScrolling(false),
      120
    );
  }

  handleOverlayMouseLeave() {
    this.setHoveredForeignDocumentDomElement(null);
  }

  handleWatchedElementMouseMove(e: MouseEvent) {
    if (!e.currentTarget) return;

    this.setHoveredForeignDocumentDomElement(
      e.currentTarget as HTMLElement | null
    );
  }

  handleWatchedElementMouseLeave(e: MouseEvent) {
    if (this.$el.contains(e.relatedTarget as Node | null)) {
      if (this.hoveredForeignDocumentDomElement !== null) {
        this.hoveredForeignDocumentDomElementCoords = this.getCoords();
      }
    } else {
      this.setHoveredForeignDocumentDomElement(null);
    }
  }

  openBuilderUrl() {
    const builderUrl = this.foreignDocumentBuilderUrl;
    if (builderUrl) {
      this.$emit("openDocument", {
        builderUrl,
        title: this.foreignDocumentTitle
      });
    }
  }
}
